'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { PageLoading } from '@/components/ui/loading';
import { apiClient } from '@/lib/api-client';
import { clearAuthStorage, getAccessToken, hasStoredUser } from '@/lib/auth-storage';
import { isAuthExemptPublicRoute } from '@/lib/i18n/public-serbian-routes';

/**
 * Access JWT is memory-only. After a full page reload the HttpOnly refresh cookie
 * must restore it before any authenticated fetch runs (Layout/UserSettings/dashboard).
 * Also re-runs when leaving a public route (e.g. soft-nav from /login) so restore
 * is not skipped just because the first mount was auth-exempt.
 */
export default function SessionRestoreProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const [ready, setReady] = useState(false);
  const [needsRestore, setNeedsRestore] = useState(false);
  let t1;
  let t2;
  if ($[0] !== pathname) {
    t1 = () => {
      let cancelled = false;
      const restore = async () => {
        if (typeof window === "undefined") {
          return;
        }
        if (isAuthExemptPublicRoute(pathname)) {
          if (!cancelled) {
            setNeedsRestore(false);
            setReady(true);
          }
          return;
        }
        if (!getAccessToken() && hasStoredUser()) {
          if (!cancelled) {
            setReady(false);
            setNeedsRestore(true);
          }
          try {
            await apiClient.refreshToken();
          } catch {
            clearAuthStorage();
          }
        }
        if (!cancelled) {
          setNeedsRestore(false);
          setReady(true);
        }
      };
      restore();
      return () => {
        cancelled = true;
      };
    };
    t2 = [pathname];
    $[0] = pathname;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!ready) {
    let t3;
    if ($[3] !== needsRestore) {
      t3 = needsRestore ? <PageLoading fullScreen={true} /> : null;
      $[3] = needsRestore;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== children) {
    t3 = <>{children}</>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}